import React, { useState, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import { min, max, add, parseISO } from "date-fns";
import PrimaryDatePicker from "../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import StyledPopover from "../../../StyledPopover/StyledPopover";
import FilterButton from "../../../Buttons/FilterButton/FilterButton";
import PrimaryRadio from "../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import { getBeginningOfDayISOString, getEndOfDayISOString } from "../../../../utils/dateHelpers";

export default function DateRangeFilter({
  column: { filterValue, preFilteredRows, id, setFilter, Header },
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [radioSelectValue, setRadioSelectValue] = useState(null);
  const [daysOutValue, setDaysOutValue] = useState(0);

  const open = Boolean(anchorEl);
  const pid = open ? "date-range-filter-popover" : undefined;

  const [minDefaultDate, maxDefaultDate] = useMemo(() => {
    const dateArray = [];
    preFilteredRows.forEach(row => {
      if (row.values[id]) {
        dateArray.push(parseISO(row.values[id]));
      }
    });

    const minDefaultDate = min(dateArray);
    const maxDefaultDate = max(dateArray);

    return [minDefaultDate, maxDefaultDate];
  }, [id, preFilteredRows]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleManualMinChange = date => {
    setRadioSelectValue(null);
    setMinValue(date);
  };

  const handleManualMaxChange = date => {
    setRadioSelectValue(null);
    setMaxValue(date);
  };

  const handleDaysOutValueChange = value => {
    setDaysOutValue(value);
    setRadioSelectValue("day-count");
  };

  const handleEnter = () => {
    setFilter([
      minValue ? getBeginningOfDayISOString(minValue) : undefined,
      maxValue ? getEndOfDayISOString(maxValue) : undefined
    ]);
    handleClose();
  };

  const handleClear = () => {
    setMinValue(minDefaultDate);
    setMaxValue(maxDefaultDate);
    setRadioSelectValue(null);
    setDaysOutValue(0);
    setFilter(undefined);
    handleClose();
  };

  useEffect(() => {
    setMinValue(minDefaultDate);
    setMaxValue(maxDefaultDate);

  }, [minDefaultDate, maxDefaultDate]);

  useEffect(() => {
    const today = new Date();

    if (radioSelectValue === "today") {
      setMinValue(parseISO(getBeginningOfDayISOString(today)));
      setMaxValue(parseISO(getEndOfDayISOString(today)));
    } else if (radioSelectValue === "tomorrow") {
      const tomorrow = add(today, {days: 1});
      setMinValue(parseISO(getBeginningOfDayISOString(tomorrow)));
      setMaxValue(parseISO(getEndOfDayISOString(tomorrow)));
    } else if (radioSelectValue === "day-count") {
      const endDate = add(today, {days: daysOutValue});
      setMinValue(parseISO(getBeginningOfDayISOString(today)));
      setMaxValue(parseISO(getEndOfDayISOString(endDate)));
    }
  }, [radioSelectValue, daysOutValue]);

  return (
    <>
      <FilterButton 
        filterValue={filterValue}
        onClick={handleClick}
      />
      <StyledPopover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        title={`Sort by ${Header}`}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ margin: "20px 0 25px 0" }}>
          <FormControl>
            <RadioGroup
              aria-labelledby="carrier-search-radio-buttons"
              name="row-radio-buttons-group"
              value={radioSelectValue}
              onChange={(e) => setRadioSelectValue(e.target.value)}
            >
              <FormControlLabel
                value={"today"}
                control={<PrimaryRadio />}
                label={<Typography variant="body1" color="black_solid.80">Today</Typography>}
              />
              <FormControlLabel
                value={"tomorrow"}
                control={<PrimaryRadio />}
                label={<Typography variant="body1" color="black_solid.80">Tomorrow</Typography>}
              />
              <FormControlLabel
                value={"day-count"}
                control={<PrimaryRadio />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ marginRight: "10px"}}>
                      <PrimaryTextField 
                        id="date-range-filter-days-out-value"
                        width="86px"
                        enableIncrement
                        isInteger
                        maxLength="4"
                        value={daysOutValue}
                        onChange={handleDaysOutValueChange}
                        onEnter={handleEnter}
                      />
                    </Box>
                    <Typography variant="body1" color="black_solid.80">days out</Typography>
                  </Box>  
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Divider sx={{ margin: "10px 0 20px 0", backgroundColor: "black_solid.30" }} />
        <Box sx={{ margin: "20px 0" }}>
          <Typography variant="body2" color="primary.60">Custom Range</Typography>
        </Box>
        <Box sx={{ padding: "10px 0" }}>
          <PrimaryDatePicker
            value={minValue}
            handleChange={handleManualMinChange}
            width="260px"
            onEnter={handleEnter}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" color="black_solid.50">
            to
          </Typography>
        </Box>
        <Box sx={{ padding: "10px 0 20px 0" }}>
          <PrimaryDatePicker
            value={maxValue}
            handleChange={handleManualMaxChange}
            width="260px"
            onEnter={handleEnter}
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          {filterValue && 
            <SecondaryButton 
              label="Clear"
              onClick={handleClear}
            />
          }
          <Box sx={{ margin: "0 20px 0 auto"}}>
            <SecondaryButton 
              label="Cancel"
              onClick={handleClose}
            />
          </Box>
          <PrimaryButton 
            label="Go"
            onClick={handleEnter}
            width="50px"
          />
        </Box>
      </StyledPopover>
    </>
  );
}

DateRangeFilter.propTypes = {
  column: PropTypes.object.isRequired
};
